import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ProductCard from '../../components/base/ProductCard';
import LoadMoreButton from '../../components/base/LoadMoreButton';
import SEO from '../../components/SEO';
import { fetchProducts, fetchMoreProducts, formatPrice, type Product } from '../../utils/api';
import { usePagination } from '../../hooks/usePagination';
import { getCategoryByPath, categories } from '../../data/categories';
import { absoluteUrl, getProductPath } from '../../utils/urls';
import NotFound from '../NotFound';

export default function CategoryPage() {
  const { pathname } = useLocation();
  const category = getCategoryByPath(pathname);
  const [sortBy, setSortBy] = useState('newest');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const { pagination, loadMore, reset, updateTotalCount } = usePagination({
    initialLimit: 20,
    onLoadMore: async (page, limit) => {
      if (!category) return { products: [], hasMore: false, totalCount: 0 };
      const result = await fetchMoreProducts({
        page,
        limit,
        category: category.dbName,
        sort: sortBy,
      });
      setProducts(prev => [...prev, ...result.products]);
      return result;
    },
  });

  useEffect(() => {
    if (!category) return;

    async function loadProducts() {
      setLoading(true);
      try {
        const response = await fetchProducts({
          category: category!.dbName,
          sort: sortBy,
          limit: 20
        });
        setProducts(response.data.products);
        updateTotalCount(response.data.pagination?.totalCount || 0);
      } catch (error) {
        console.error('Failed to load products:', error);
      } finally {
        setLoading(false);
      }
    }

    reset();
    setProducts([]);
    loadProducts();
  }, [category?.dbName, sortBy, reset, updateTotalCount]);

  if (!category) {
    return <NotFound />;
  }

  const sortOptions = [
    { id: 'newest', name: 'Nyeste' },
    { id: 'price-low', name: 'Pris: Lav til Høj' },
    { id: 'price-high', name: 'Pris: Høj til Lav' }
  ];

  const accentText = category.accent === 'blue' ? 'text-blue-600' : 'text-emerald-600';
  const accentBg = category.accent === 'blue' ? 'bg-blue-50' : category.accent === 'gray' ? 'bg-gray-100' : 'bg-emerald-50';
  const pageUrl = absoluteUrl(category.path);
  const otherCategories = categories.filter(c => c.id !== category.id);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category.h1,
    description: category.description,
    url: pageUrl,
    inLanguage: 'da',
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: pagination.totalCount || products.length,
      itemListElement: products.slice(0, 20).map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: absoluteUrl(getProductPath(product)),
        name: product.title
      }))
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={category.title}
        description={category.description}
        keywords={category.keywords}
        url={pageUrl}
        breadcrumbs={[
          { name: 'Hjem', url: absoluteUrl('/') },
          { name: category.name, url: pageUrl }
        ]}
        structuredData={structuredData}
      />
      <Header />
      <main>
        <section
          className="relative h-80 flex items-center justify-center bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('${category.heroImage}')`
          }}
        >
          <div className="text-center text-white max-w-4xl mx-auto px-4">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">{category.h1}</h1>
            <p className="text-xl md:text-2xl">{category.subtitle}</p>
          </div>
        </section>

        <section className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="max-w-3xl space-y-4 text-gray-700 text-lg leading-relaxed">
              {category.intro.map((paragraph) => (
                <p key={paragraph.slice(0, 40)}>{paragraph}</p>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{category.name}</h2>
                <p className="text-gray-600 mt-1">
                  {pagination.totalCount > 0
                    ? `${pagination.totalCount} produkter tilgængelige`
                    : 'Sammenlign priser fra danske forhandlere'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="category-sort" className="text-sm text-gray-600">Sorter efter:</label>
                <select
                  id="category-sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm pr-8 cursor-pointer"
                >
                  {sortOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">{category.loadingLabel}</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">{category.emptyLabel}</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {products.map((product) => (
                    <ProductCard
                      key={product.id}
                      href={getProductPath(product)}
                      name={product.title}
                      price={formatPrice(product.price, product.currency)}
                      originalPrice={product.old_price && product.old_price > product.price ? formatPrice(product.old_price, product.currency) : undefined}
                      image={product.image_url || 'https://via.placeholder.com/300x300?text=No+Image'}
                      vendor={product.shop || product.brand}
                    />
                  ))}
                </div>
                <LoadMoreButton
                  onLoadMore={loadMore}
                  loading={pagination.loading}
                  hasMore={pagination.hasMore}
                  totalCount={pagination.totalCount}
                  currentCount={products.length}
                />
              </>
            )}
          </div>
        </section>

        <section className={`py-16 ${accentBg}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">{category.guideTitle}</h2>
                <div className="space-y-4 text-lg text-gray-600">
                  {category.guide.map((paragraph) => (
                    <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                  ))}
                </div>
                <ul className="space-y-3 mt-6">
                  {category.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-center space-x-3">
                      <i className={`ri-check-line ${accentText} text-xl`}></i>
                      <span className="text-gray-700">{bullet}</span>
                    </li>
                  ))}
                </ul>
                {category.relatedBlog && (
                  <p className="mt-6">
                    <Link to={category.relatedBlog.slug} className={`${accentText} hover:underline font-medium`}>
                      {category.relatedBlog.text}
                    </Link>
                  </p>
                )}
              </div>
              <div className="aspect-[3/2]">
                <img
                  src={category.infoImage}
                  alt={category.infoImageAlt}
                  width="600"
                  height="400"
                  className="rounded-lg shadow-lg object-cover object-top w-full h-full"
                  loading="lazy"
                />
              </div>
            </div>
            <nav aria-label="Andre kategorier" className="mt-12 pt-8 border-t border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Se også</h2>
              <ul className="flex flex-wrap gap-4">
                {otherCategories.map((item) => (
                  <li key={item.id}>
                    <Link to={item.path} className={`${accentText} hover:underline font-medium`}>
                      {item.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/blog" className={`${accentText} hover:underline font-medium`}>Blog</Link>
                </li>
              </ul>
            </nav>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
